// UI-WORKBENCH-1 (2026-05-07) — Three-panel command-center Workbench v1.
// Replaces pages/ClaimsList.js (deleted in this chunk per Kuhrt 2026-05-07
// "hard-cut" decision; no classic-mode fallback).
//
// Layout (memo §15.2): left rail (filters, ~280px) / center grid (claims
// table) / right preview (~380px).  Row click → preview, no page nav.
// "Open Claim →" button in preview navigates to ClaimDetail via the
// onSelectClaim prop.
//
// v1 scope (matches BACKLOG note):
//   - Filters lifted from ClaimsList top bar into the left rail.
//   - Existing 11-column claims grid in the center.
//   - PreviewPanel in the right column reads GET /claims/{id}/preview.
//   - Saved views + queue counts deferred to UI-WORKBENCH-2.
//   - No mobile responsive layout (memo §15.2 desktop-first).
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { apiFetch, fmt, statusColor, CLAIM_TYPES, can, getUser } from "../api";
import { StatusPill, Spinner, EmptyState, SummaryRibbon, RibbonCell } from "../components/ui";  // UI-SWEEP-WORKBENCH / UI-RIBBON-1
import { SortableTh, sortRowsBy } from "../components/SortableTable";
import { AddVesselModal, AddCompanyModal, Combo } from "../components/pickers";
import CommandCenter from "../components/CommandCenter";
import PreviewPanel from "../components/PreviewPanel";
import { ReadinessBadge } from "../components/ClaimReadiness";

// Parse a FastAPI error body ({"detail": "..."}) into a readable message.
function errMsg(e) {
  try { const j = JSON.parse(e.message); return j.detail || e.message; } catch { return e.message; }
}

// Full-width group divider inside the modal form-grid — groups fields "how an
// adjuster thinks through it" (Kuhrt 2026-07-17).
function SectionLabel({ children }) {
  return <div className="form-group full" style={{ borderTop: "1px solid var(--border)", margin: "4px 0 0", paddingTop: 8, fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--text3)" }}>{children}</div>;
}

// New-claim / New-TPA-matter modal (TPA-MATTER-CREATION-1, 2026-07-17).
// Both matter types share ONE layout and the SAME number-population workflow: the
// claim number is minted server-side and shown as a live preview that follows the
// chosen lead adjuster (IA → YY-######<initials> from 004000; TPA → YY-T#####<initials>
// from 80000). TPA adds an accent banner + an IA-file link/pull section at the end.
// A lead adjuster is REQUIRED on both. On success a summary dialog links to the
// new claim; the assigned lead adjuster is notified unless they are the creator.
function NewClaimModal({ onClose, onSaved, onOpenClaim, tpa = false }) {
  const [form, setForm] = useState({
    claim_number: "", claim_status: "Open", claim_type: "Jones Act, P&I Crew",
    date_of_loss: "", loss_location: "", is_jones_act: true,
    lead_adjuster_id: "", claimant_name: "", vessel_id: "", injury: "",
  });
  const [users, setUsers] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [insured, setInsured] = useState(null);   // {id, label}
  const [iaLink, setIaLink] = useState(null);      // {id, claim_number, claimant_name}
  const [pullFromIa, setPullFromIa] = useState(true);
  const [numPreview, setNumPreview] = useState("");
  const [addVessel, setAddVessel] = useState(false);
  const [addCompany, setAddCompany] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);

  useEffect(() => {
    apiFetch("/users/assignable?lead=1").then(u => setUsers(Array.isArray(u) ? u : [])).catch(() => {});
    apiFetch("/vessels").then(v => setVessels(Array.isArray(v) ? v : [])).catch(() => {});
  }, []);

  // Both modals populate the claim number the same way: a live preview that
  // follows the chosen lead adjuster (TPA and IA are both server-minted).
  useEffect(() => {
    if (!form.lead_adjuster_id) { setNumPreview(""); return; }
    const ep = tpa ? "/claims/tpa/next-number" : "/claims/ia/next-number";
    apiFetch(`${ep}?lead_adjuster_id=${form.lead_adjuster_id}`)
      .then(r => setNumPreview(r.claim_number || "")).catch(() => setNumPreview(""));
  }, [tpa, form.lead_adjuster_id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async () => {
    if (!form.lead_adjuster_id) { setError("A lead adjuster is required on every new claim."); return; }
    setSaving(true); setError("");
    const body = {
      claim_status: form.claim_status, claim_type: form.claim_type,
      date_of_loss: form.date_of_loss || null, loss_location: form.loss_location || null,
      is_jones_act: form.is_jones_act, lead_adjuster_id: Number(form.lead_adjuster_id),
      claimant_name: form.claimant_name || null,
      insured_company_id: insured ? insured.id : null,
      vessel_id: form.vessel_id ? Number(form.vessel_id) : null,
      injury: form.injury || null,
    };
    if (tpa && iaLink) { body.link_ia_claim_id = iaLink.id; body.pull_from_ia = pullFromIa; }
    try {
      const res = await apiFetch(tpa ? "/claims/tpa" : "/claims", { method: "POST", body });
      const lead = users.find(u => String(u.id) === String(form.lead_adjuster_id));
      const me = getUser();
      setCreated({
        ...res, tpa, iaLink,
        entered: { ...body, lead_name: lead && lead.full_name, insured_name: insured && insured.label,
                   vessel_name: (vessels.find(v => String(v.id) === String(form.vessel_id)) || {}).vessel_name },
        notifiedLead: lead && me && Number(lead.id) !== Number(me.id) ? lead.full_name : null,
      });
      onSaved();
    } catch (e) { setError(errMsg(e)); setSaving(false); }
  };

  if (created) {
    return <CreatedClaimModal data={created} onClose={onClose}
             onOpenClaim={() => { onClose(); onOpenClaim && onOpenClaim(created.id); }} />;
  }

  const accent = tpa ? { background: "#ede9fe", color: "#4c1d95", border: "1px solid #c4b5fd" } : null;
  return (<>
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-header"><h3>{tpa ? "New TPA Matter" : "New Claim"}</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          {error && <div className="alert-error">{error}</div>}
          {tpa && <div className="alert-error" style={accent}>TPA matter — an independent claim number is assigned automatically (<strong>YY-T#####<i>initials</i></strong>). Only TPA-authorized users will see this matter.</div>}
          <div className="form-grid">
            {/* Row 1 — Claim Number + Status. Both matter types mint the number
                the same way: a live preview that follows the lead adjuster. */}
            <div className="form-group"><label>Claim Number</label>
              <input value={numPreview || "— pick a lead adjuster —"} disabled
                title="Assigned automatically when you create the claim"
                style={{ fontWeight: 600, letterSpacing: ".5px", color: numPreview ? "inherit" : "var(--text3)" }} /></div>
            <div className="form-group"><label>Status</label>
              <select value={form.claim_status} onChange={set("claim_status")}><option>Open</option><option>On Hold</option><option>Closed</option></select></div>

            {/* Row 2 — Lead Adjuster + Claim Type */}
            <div className="form-group"><label>Lead Adjuster *</label>
              <select value={form.lead_adjuster_id} onChange={set("lead_adjuster_id")} autoFocus>
                <option value="">— select —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select></div>
            <div className="form-group"><label>Claim Type</label>
              <select value={form.claim_type} onChange={set("claim_type")}>{CLAIM_TYPES.map(t => <option key={t}>{t}</option>)}</select></div>

            <SectionLabel>Insured &amp; Vessel</SectionLabel>
            <div className="form-group"><label>Insured (policy-holding company)</label>
              <Combo placeholder="Search companies…"
                fetcher={q => apiFetch(`/companies?search=${encodeURIComponent(q)}`).then(rows => (rows || []).map(c => ({ id: c.id, label: c.company_name, sub: [c.city, c.state].filter(Boolean).join(", ") })))}
                value={insured ? insured.id : null} display={insured ? insured.label : null}
                onPick={c => setInsured({ id: c.id, label: c.label })} onClear={() => setInsured(null)}
                onAddNew={() => setAddCompany(true)} addLabel="+ Add new company" /></div>
            <div className="form-group"><label>Vessel</label>
              <Combo placeholder="Search vessels…"
                options={vessels.map(v => ({ id: v.id, label: v.vessel_name }))}
                value={form.vessel_id || null} display={(vessels.find(v => String(v.id) === String(form.vessel_id)) || {}).vessel_name}
                onPick={v => setForm(f => ({ ...f, vessel_id: v.id }))} onClear={() => setForm(f => ({ ...f, vessel_id: "" }))}
                onAddNew={() => setAddVessel(true)} addLabel="+ Add new vessel" /></div>

            <SectionLabel>Loss &amp; Injury</SectionLabel>
            <div className="form-group"><label>Claimant Name</label>
              <input value={form.claimant_name} onChange={set("claimant_name")} placeholder="Injured crewmember" /></div>
            <div className="form-group"><label>Date of Loss</label>
              <input type="date" value={form.date_of_loss} onChange={set("date_of_loss")} /></div>
            <div className="form-group"><label>Injury / Illness</label>
              <input value={form.injury} onChange={set("injury")} placeholder="e.g. Lower back strain" /></div>
            <div className="form-group"><label>Loss Location</label>
              <input value={form.loss_location} onChange={set("loss_location")} placeholder="Location of incident" /></div>

            {tpa && <>
              <SectionLabel>Link to an existing IA matter (optional)</SectionLabel>
              <div className="form-group full">
                <Combo placeholder="Search IA claim # or claimant…"
                  fetcher={q => apiFetch(`/tpa/ia-lookup?q=${encodeURIComponent(q)}`).then(rows => (rows || []).map(c => ({ id: c.id, label: c.claim_number, sub: c.claimant_name, _raw: c })))}
                  value={iaLink ? iaLink.id : null}
                  display={iaLink ? `${iaLink.claim_number}${iaLink.claimant_name ? " — " + iaLink.claimant_name : ""}` : null}
                  onPick={c => setIaLink(c._raw || c)} onClear={() => setIaLink(null)} />
                {iaLink && <>
                  <label style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 8, fontWeight: 400, fontSize: 13 }}>
                    <input type="checkbox" checked={pullFromIa} onChange={e => setPullFromIa(e.target.checked)} style={{ width: "auto" }} />
                    Pull claim info, loss, parties &amp; coverage from this IA file (one-time, on create)
                  </label>
                  <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Linking does not change this matter's claim number. Financials, time, documents, reports &amp; activity are never pulled — these stay separate matters.</div>
                </>}
              </div>
            </>}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Creating…" : (tpa ? "Create TPA Matter" : "Create Claim")}</button>
        </div>
      </div>
    </div>
    {addVessel && <AddVesselModal onClose={() => setAddVessel(false)}
      onCreated={v => { setVessels(prev => [...prev, v].sort((a, b) => (a.vessel_name || "").localeCompare(b.vessel_name || ""))); setForm(f => ({ ...f, vessel_id: String(v.id) })); setAddVessel(false); }} />}
    {addCompany && <AddCompanyModal onClose={() => setAddCompany(false)}
      onCreated={c => { setInsured({ id: c.id, label: c.company_name }); setAddCompany(false); }} />}
  </>);
}

// Success dialog after a claim/matter is created (TPA-MATTER-CREATION-1).
function CreatedClaimModal({ data, onClose, onOpenClaim }) {
  const e = data.entered || {};
  const pulled = data.linked && data.linked.pulled;
  const Row = ({ label, value }) => value ? (
    <div style={{ display: "flex", gap: 10, fontSize: 13, padding: "3px 0" }}>
      <span style={{ width: 130, color: "var(--text3)", flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>) : null;
  return (
    <div className="modal-overlay" onClick={ev => ev.target === ev.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header"><h3>{data.tpa ? "TPA Matter Created" : "Claim Created"}</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: ".5px", marginBottom: 10 }}>{data.claim_number}</div>
          <Row label="Lead Adjuster" value={e.lead_name} />
          <Row label="Claimant" value={e.claimant_name} />
          <Row label="Insured" value={e.insured_name} />
          <Row label="Vessel" value={e.vessel_name} />
          <Row label="Injury / Illness" value={e.injury} />
          <Row label="Claim Type" value={e.claim_type} />
          {data.iaLink && <Row label="Linked IA file" value={data.iaLink.claim_number} />}
          {pulled && <div style={{ marginTop: 8, padding: "8px 10px", background: "var(--bg2)", borderRadius: 5, fontSize: 12, color: "var(--text2)" }}>
            Pulled from the IA file: {pulled.parties} part{pulled.parties === 1 ? "y" : "ies"}, {pulled.policies} polic{pulled.policies === 1 ? "y" : "ies"}, {pulled.coverages} coverage line{pulled.coverages === 1 ? "" : "s"}{pulled.claimant ? ", claimant details" : ""}.
          </div>}
          {data.notifiedLead && <div style={{ marginTop: 10, fontSize: 12, color: "var(--text3)" }}>🔔 {data.notifiedLead} has been notified they are the lead adjuster.</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={onOpenClaim}>Open Claim →</button>
        </div>
      </div>
    </div>);
}

// WORKBENCH-INSURED-LIFT (2026-05-11): Insured = policy-holding company
// (from /claims insured_name, claim_parties-sourced); Claimant = injured
// person (from claimant_name).  Prior shape had VCA labels inverted.
// CLAIMS-WORKSPACE-FINISH-1: triage signal. Each attention type → dot color +
// label, in severity order (first match colors the row's dot).
const ATTN_META = {
  overdue_tasks:  { color: "#dc2626", label: "Overdue task" },
  reserve_reviews:{ color: "#d97706", label: "Reserve review due" },
  new_documents:  { color: "#7c3aed", label: "New document" },
  my_open_tasks:  { color: "#2563eb", label: "High-priority task" },
  stale_claims:   { color: "#6b7280", label: "Inactive" },
};
const ATTN_ORDER = ["overdue_tasks", "reserve_reviews", "new_documents", "my_open_tasks", "stale_claims"];

// Compact "how long ago" for the Last Activity column.
function agoLabel(d) {
  if (!d) return null;
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "1d";
  if (days < 45) return `${days}d`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
}

const cellClass = (align) => align === "right" ? "text-right mono" : align === "center" ? "text-center" : undefined;

function renderCell(col, c, attnByClaim) {
  switch (col.key) {
    case "claim_number": {
      const types = attnByClaim.get(c.id) || [];
      const top = ATTN_ORDER.find(t => types.includes(t));
      const dot = top
        ? <span title={types.map(t => ATTN_META[t].label).join(" · ")}
                style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: ATTN_META[top].color, marginRight: 7, verticalAlign: "middle", flexShrink: 0 }} />
        : <span style={{ display: "inline-block", width: 7, marginRight: 7 }} />;
      return <>{dot}<span className="mono" style={{ color: "var(--blue)", fontWeight: 600, fontSize: 12 }}>{c.claim_number}</span></>;
    }
    case "insured_name":  return c.insured_name || "—";
    case "claimant_name": return c.claimant_name || "—";
    case "date_of_loss":  return fmt.date(c.date_of_loss);
    case "last_activity_at": {
      if (!c.last_activity_at) return <span style={{ color: "var(--text3)" }}>—</span>;
      const days = Math.floor((Date.now() - new Date(c.last_activity_at).getTime()) / 86400000);
      const cold = days >= 30 && c.claim_status === "Open";
      return <span title={fmt.date(c.last_activity_at)} style={{ color: cold ? "var(--text3)" : "inherit", fontStyle: cold ? "italic" : "normal" }}>{agoLabel(c.last_activity_at)}</span>;
    }
    case "lead_adjuster_name": return c.lead_adjuster_name || "—";
    case "vessel_name":   return c.vessel_name || "—";
    case "claim_status":  return <StatusPill sm status={c.claim_status} />;
    case "total_reserves": return fmt.money(c.total_reserves);
    case "total_paid":     return fmt.money(c.total_paid);
    // WORKBENCH-COLS-3 (2026-06-19): Total Reserve over Total Payments stacked in
    // one column — reserve (primary) on top, payments (muted) beneath.
    case "reserves_payments": return (
      <div style={{ lineHeight: 1.2, whiteSpace: "nowrap" }}>
        <div className="mono">{fmt.money(c.total_reserves)}</div>
        <div className="mono" style={{ fontSize: 11, color: "var(--text3)" }} title="Total Payments">{fmt.money(c.total_paid)}</div>
      </div>
    );
    case "remaining": {
      const r = parseFloat(c.total_reserves || 0) - parseFloat(c.total_paid || 0);
      const open = c.claim_status === "Open";
      const color = r < 0 ? "var(--red)" : (r === 0 && open ? "#d97706" : "inherit");
      return <span style={{ color, fontWeight: 600 }}>{fmt.money(r)}{r === 0 && open ? " ⚠" : ""}</span>;
    }
    case "setup_readiness": return <ReadinessBadge level={c.setup_readiness} />;
    // WORKBENCH-COLS-2 (2026-06-19): Status + Readiness in one narrow column —
    // the status badge with a compact readiness dot (hover = readiness level).
    case "status_readiness": return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
        <StatusPill sm status={c.claim_status} />
        <ReadinessBadge level={c.setup_readiness} compact />
      </span>
    );
    case "open_tasks": return c.open_tasks > 0 ? <span className="badge" style={{ background: "#fef3c7", color: "#92400e" }}>{c.open_tasks}</span> : null;
    default: return c[col.key] ?? "—";
  }
}

// WORKBENCH-COLS-2 (2026-06-19): narrowed column set per Kuhrt — Claim #,
// Insured, Claimant, DOL, Adjuster, Last Activity, Total Reserve, Status (with a
// compact Readiness dot), Tasks. Vessel / Total Payments / Remaining Reserve
// dropped to reclaim horizontal space (rail on the left + preview on the right).
const COLUMNS = [
  { key: "claim_number",       label: "Claim #",       align: undefined },
  { key: "insured_name",       label: "Insured",       align: undefined },
  { key: "claimant_name",      label: "Claimant",      align: undefined },
  { key: "date_of_loss",       label: "DOL",           align: undefined },
  { key: "lead_adjuster_name", label: "Adjuster",      align: undefined },
  { key: "last_activity_at",   label: "Activity",      align: undefined },
  { key: "reserves_payments",  label: "Reserve / Paid", align: "right"  },
  { key: "status_readiness",   label: "Status",        align: undefined },
  { key: "open_tasks",         label: "Tasks",         align: "center"  },
];

export default function Workbench({ onSelectClaim, currentUser, workRefresh }) {
  const [claims, setClaims]                     = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [search, setSearch]                     = useState("");
  const [statusFilter, setStatusFilter]         = useState("Open");
  // Default to "All Adjusters" for the oversight roles — executive / ownership /
  // system_admin, which are EXACTLY the holders of view_sysadmin_panel (Kuhrt
  // 2026-06-02). /me doesn't carry role_codes, so we key off the cap.
  const [adjusterFilter, setAdjusterFilter]     = useState(can("view_sysadmin_panel") ? "all" : "mine");
  // WORKBENCH-ADJUSTER-SELECT (2026-06-18): oversight roles (view_all_claims —
  // adjusters and TPA users never hold it) get a per-adjuster selector.
  const [adjusterOptions, setAdjusterOptions]   = useState([]);
  const [matterTypeFilter, setMatterTypeFilter] = useState("all");
  const [readinessFilter, setReadinessFilter]   = useState("all");
  const [showNew, setShowNew]                   = useState(false);
  const [showNewTpa, setShowNewTpa]             = useState(false);
  const [sort, setSort]                         = useState({ key: "claim_number", dir: "asc" });
  const [selectedClaimId, setSelectedClaimId]   = useState(null);
  // OPS-1 Action Queue: per-reason claim-id lists; a chip filters the grid.
  const [attention, setAttention]               = useState(null);
  const [activeChip, setActiveChip]             = useState(null);

  // Stale-response guard (SEED-DATA-1 find #4): the unfiltered 641-row fetch is
  // slow (reserve subqueries); a fast filtered fetch fired later could be
  // clobbered when the old response landed. Only the latest request may write.
  const loadSeq = useRef(0);
  const load = useCallback(() => {
    setLoading(true);
    const seq = ++loadSeq.current;
    let url = "/claims?limit=2000";  // 641 open claims today; 500 cap was hiding the tail (found via SEED-DATA-1)
    if (statusFilter) url += `&status=${encodeURIComponent(statusFilter)}`;
    if (search)       url += `&search=${encodeURIComponent(search)}`;
    if (matterTypeFilter && matterTypeFilter !== "all") url += `&matter_type=${matterTypeFilter}`;
    if (readinessFilter && readinessFilter !== "all") url += `&readiness=${readinessFilter}`;
    // A numeric adjusterFilter = a specific lead adjuster; scope server-side.
    // "mine"/"all" are handled client-side below.
    if (/^\d+$/.test(String(adjusterFilter))) url += `&adjuster_id=${adjusterFilter}`;
    apiFetch(url).then(d => { if (seq === loadSeq.current) { setClaims(d); setLoading(false); } })
                 .catch(() => { if (seq === loadSeq.current) setLoading(false); });
  }, [search, statusFilter, matterTypeFilter, readinessFilter, adjusterFilter, workRefresh]);

  useEffect(() => { load(); }, [load]);

  // Adjuster selector options — distinct lead adjusters across visible claims.
  useEffect(() => {
    if (!can("view_all_claims")) return;
    apiFetch("/claims/lead-adjusters").then(d => setAdjusterOptions(d || [])).catch(() => setAdjusterOptions([]));
  }, []);

  // Action Queue counts/lists — refresh on mount + whenever work changes.
  useEffect(() => {
    apiFetch("/workbench/attention").then(setAttention).catch(() => setAttention(null));
  }, [workRefresh]);

  // PERMISSIONS-CLAIMS-SCOPE (2026-05-29): only VIEW_ALL_CLAIMS holders may
  // browse claims they aren't assigned to (everyone except the `adjuster`
  // role).  For adjusters the backend already scopes /claims to their own
  // (lead OR active claim_staff), so we show every returned row and do NOT
  // apply the fragile client-side name filter — that filter only matched
  // lead_adjuster_name, which would have hidden claims where the user is
  // following staff but not the lead.
  const canViewAll = can("view_all_claims");
  const userName = currentUser?.full_name || "";
  const filtered = (canViewAll && adjusterFilter === "mine")
    ? claims.filter(c => {
        const adj = c.lead_adjuster_name || "";
        return adj.toLowerCase().includes(userName.split(" ")[0].toLowerCase());
      })
    : claims;

  // Only show the Adjuster column when the view actually spans adjusters; a
  // self-scoped ("mine") or single-adjuster view repeats one value — pure noise.
  const showAdjuster = canViewAll && adjusterFilter === "all";

  // claim_id → attention types (from /workbench/attention) for the row dot.
  const attnByClaim = useMemo(() => {
    const m = new Map();
    if (attention) for (const [type, ids] of Object.entries(attention)) {
      if (!ATTN_META[type]) continue;
      for (const id of (ids || [])) {
        if (!m.has(id)) m.set(id, []);
        m.get(id).push(type);
      }
    }
    return m;
  }, [attention]);

  // Drop the Adjuster column when the view is self-scoped or a single adjuster
  // (every row repeats one value — pure noise).
  const columns = useMemo(
    () => showAdjuster ? COLUMNS : COLUMNS.filter(x => x.key !== "lead_adjuster_name"),
    [showAdjuster]
  );

  const sortAccessors = useMemo(() => ({
    date_of_loss:  c => c.date_of_loss ? new Date(c.date_of_loss).getTime() : null,
    last_activity_at: c => c.last_activity_at ? new Date(c.last_activity_at).getTime() : 0,
    status_readiness: c => c.claim_status || "",
    reserves_payments: c => parseFloat(c.total_reserves) || 0,
    total_reserves:c => parseFloat(c.total_reserves) || 0,
    total_paid:    c => parseFloat(c.total_paid)     || 0,
    remaining:     c => (parseFloat(c.total_reserves)||0) - (parseFloat(c.total_paid)||0),
    open_tasks:    c => parseInt(c.open_tasks, 10) || 0,
  }), []);

  const sorted = sortRowsBy(filtered, sort.key, sort.dir, sortAccessors);

  // Action Queue: chip definitions + grid filtering by the selected reason.
  const CHIP_DEFS = [
    { key: "overdue_tasks",   label: "Overdue Tasks",   color: "#dc2626" },
    { key: "reserve_reviews", label: "Reserve Reviews", color: "#d97706" },
    { key: "my_open_tasks",   label: "High Priority",   color: "#2563eb" },
    { key: "new_documents",   label: "Documents",       color: "#7c3aed" },
    { key: "stale_claims",    label: "Inactive Claims", color: "#6b7280" },
  ];
  const activeSet = (activeChip && attention) ? new Set(attention[activeChip] || []) : null;
  const displayed = activeSet ? sorted.filter(c => activeSet.has(c.id)) : sorted;

  // ─── Left rail: filters + counts ───
  const rail = (
    <>
      <div className="cc-rail-section" style={{ marginBottom: 2 }}>
        <div className="cc-rail-label">Filters</div>
      </div>
      <div className="cc-rail-section">
        <div className="cc-rail-label">Search</div>
        <input className="cc-rail-input"
               placeholder="Claim #, insured, claimant..."
               value={search}
               onChange={e => setSearch(e.target.value)}/>
      </div>
      <div className="cc-rail-section">
        <div className="cc-rail-label">Status</div>
        <select className="cc-rail-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="Open">Open</option>
          <option value="Closed">Closed</option>
          <option value="On Hold">On Hold</option>
          <option value="Litigation">Litigation</option>
        </select>
      </div>
      <div className="cc-rail-section">
        <div className="cc-rail-label">Readiness</div>
        <select className="cc-rail-select" value={readinessFilter} onChange={e => setReadinessFilter(e.target.value)}>
          <option value="all">All Readiness</option>
          <option value="ready">Ready</option>
          <option value="partially_ready">Partially Ready</option>
          <option value="not_ready">Not Ready</option>
        </select>
      </div>
      {canViewAll && (
        <div className="cc-rail-section">
          <div className="cc-rail-label">Adjuster</div>
          <select className="cc-rail-select" value={adjusterFilter} onChange={e => setAdjusterFilter(e.target.value)}>
            <option value="mine">My Claims</option>
            <option value="all">All Adjusters</option>
            {adjusterOptions.length > 0 && <option disabled>──────────</option>}
            {adjusterOptions.map(a => (
              <option key={a.id} value={String(a.id)}>{a.full_name} ({a.claim_count})</option>
            ))}
          </select>
        </div>
      )}
      {can("view_tpa_files") && (
        <div className="cc-rail-section">
          <div className="cc-rail-label">Matter Type</div>
          <select className="cc-rail-select" value={matterTypeFilter} onChange={e => setMatterTypeFilter(e.target.value)}>
            <option value="all">IA + TPA</option>
            <option value="ia">IA only</option>
            <option value="tpa">TPA files</option>
          </select>
        </div>
      )}
      <div className="cc-rail-section" style={{ marginTop: 4 }}>
        <button className="btn btn-blue" onClick={() => setShowNew(true)}>+ New Claim</button>
        {can("create_tpa_file") && <button className="btn btn-outline" onClick={() => setShowNewTpa(true)}>+ New TPA Matter</button>}
      </div>
      <div className="cc-rail-count">{loading ? "Loading..." : `${displayed.length}${activeChip ? ` / ${sorted.length}` : ""} claims`}</div>
    </>
  );

  // ─── Action Queue chip row (OPS-1) ───
  const chipRow = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "10px 12px", borderBottom: "1px solid var(--border)", background: "#fafbfc", alignItems: "center" }}>
      {CHIP_DEFS.map(def => {
        const n = attention ? (attention[def.key] || []).length : 0;
        if (n === 0 && activeChip !== def.key) return null;
        const on = activeChip === def.key;
        return (
          <button key={def.key} onClick={() => setActiveChip(on ? null : def.key)}
                  style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 11px", borderRadius: 16, cursor: "pointer",
                           border: `1px solid ${def.color}${on ? "" : "44"}`, background: on ? def.color : "#fff",
                           color: on ? "#fff" : "var(--text1, #1a2438)", fontSize: 12, fontWeight: 600 }}>
            <span style={{ fontWeight: 800 }}>{n}</span>{def.label}
          </button>
        );
      })}
      {activeChip && <button onClick={() => setActiveChip(null)} style={{ background: "transparent", border: "none", color: "var(--text3)", cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>clear</button>}
      {attention && CHIP_DEFS.every(d => (attention[d.key] || []).length === 0) && (
        <span style={{ fontSize: 12, color: "var(--text3)" }}>✓ Nothing needs attention</span>
      )}
    </div>
  );

  // ─── Center grid ───
  // UI-RIBBON-1 (LD-DESIGN-010): Workbench vitals — computed from the loaded grid.
  const wbRibbon = (
    <SummaryRibbon style={{ margin: "10px 14px 0", marginBottom: 0 }}>
      {[
        <RibbonCell key="n" icon="🗂" label="Claims" value={String(displayed.length)}
          sub={displayed.length !== claims.length ? `of ${claims.length} loaded` : undefined} />,
        <RibbonCell key="o" label="Open" value={String(displayed.filter(c => c.claim_status === "Open").length)} color="var(--green)" />,
        <RibbonCell key="t" icon="✓" label="Open Tasks" value={String(displayed.reduce((s, c) => s + (Number(c.open_tasks) || 0), 0))} />,
      ]}
    </SummaryRibbon>
  );
  const grid = (
    <>
      {wbRibbon}
      {chipRow}
      <div className="cc-grid-body card table-wrap" style={{ borderRadius: 0, border: "none" }}>
        <table>
          <thead>
            <tr>
              {columns.map(col => (
                <SortableTh key={col.key} label={col.label} col={col.key} align={col.align} sort={sort} setSort={setSort}/>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={columns.length}><Spinner label="Loading claims…" /></td></tr>
            : displayed.length === 0 ? <tr><td colSpan={columns.length} style={{ padding: 0 }}>
                <EmptyState
                  icon={activeChip ? "\u25C9" : (search || statusFilter || readinessFilter !== "all" || matterTypeFilter !== "all") ? "\u2315" : "\u25A1"}
                  title={activeChip ? "No loaded claims match this attention filter"
                    : (search || statusFilter || readinessFilter !== "all" || matterTypeFilter !== "all") ? "No claims match your filters"
                    : "No claims yet"}
                  hint={activeChip ? "Clear the chip above to see all loaded claims."
                    : (search || statusFilter || readinessFilter !== "all" || matterTypeFilter !== "all") ? "Try widening Status or Readiness, or clearing Search."
                    : "Create one with + New Claim in the left rail."} /></td></tr>
            : displayed.map(c => (
              <tr key={c.id}
                  className={`tr-link ${selectedClaimId === c.id ? "tr-selected" : ""}`}
                  onClick={() => setSelectedClaimId(c.id)}>
                {columns.map(col => (
                  <td key={col.key} className={cellClass(col.align)}>{renderCell(col, c, attnByClaim)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );

  // ─── Right preview ───
  const preview = (
    <PreviewPanel claimId={selectedClaimId} onOpenClaim={onSelectClaim} refreshKey={workRefresh}/>
  );

  return (
    <div className="page workbench-page">
      <CommandCenter rail={rail} grid={grid} preview={preview}/>
      {showNew && <NewClaimModal onClose={() => setShowNew(false)} onSaved={() => load()} onOpenClaim={onSelectClaim}/>}
      {showNewTpa && <NewClaimModal tpa onClose={() => setShowNewTpa(false)} onSaved={() => load()} onOpenClaim={onSelectClaim}/>}
    </div>
  );
}
